import { useState, useEffect } from "react";
import CustomDiscountDisplay from "./CustomDiscountDisplay";
import CustomOutOfStockDisplay from "./CustomOutOfStockDisplay";
import CustomPriceDisplay from "./CustomPriceDisplay";
import { Fade } from "react-reveal";
import { alreadyAdded } from "../helpers/products";
import useUser from "../hooks/useUser";
import useDeleteWishlistItem from "../hooks/useDeleteWishlistItem";
import useAddToWishlist from "../hooks/useAddToWishlist";
import { IoHeartSharp } from "react-icons/io5";
import classNames from "classnames";
import CustomSnackBar from "./CustomSnackBar";
import { formatNumber } from "../utils";
import { BsCartPlus } from "react-icons/bs";
import GeneralModal from "./GeneralModal";
import ProductDetailsSection from "../pages/product/ProductDetailsSection";
import { useWishlist } from "../contexts/WishlistContext";

function ProductItemMini({ product }) {
  const [currentImage, setCurrentImage] = useState(product.imageOne);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showQuickCart, setShowQuickCart] = useState(false);
  const [isAdded, setIsAdded] = useState(false); // Track wishlist status
  const { wishlist, addToWishlist, removeFromWishlist } = useWishlist();

  const { user } = useUser();
  const { deleteWishListItem } = useDeleteWishlistItem(user);
  const { addToUserWishlist } = useAddToWishlist();


  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [isError, setIsError] = useState(false);

  const details = {
    productId: product.id,
    color: null,
    userId: user?.id,
    accessToken: user?.accessToken,
  };

  // Check if the product is in the wishlist (local storage or server)
  useEffect(() => {
    const isInWishlist = wishlist.some((item) => item.product.id === product.id);
    const isAdded = alreadyAdded(product?.Wishlists, product?.id)

    setIsAdded(isInWishlist || isAdded);
  }, [wishlist, product]);

  // Add to wishlist
  const handleAddToWishlist = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (user) {
      let modifiedDetails = details;

      setIsAdding(true);
      addToUserWishlist(modifiedDetails)
        .then((res) => {
          setIsError(false);
          setSnackBarMessage("Successfully added to wishlist");
          setIsAdded(true); // Update wishlist status
        })
        .catch((err) => {
          setIsError(true);
          setSnackBarMessage(err?.message);
        })
        .finally(() => {
          setOpenSnackBar(true);
          setIsAdding(false);
        });
    } else {
      addToWishlist(product);
      setOpenSnackBar(true);
      setSnackBarMessage("Successfully added to wishlist");

    }
  };

  // Remove from wishlist
  const handleDeleteItem = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (user) {
      setIsDeleting(true);
      deleteWishListItem({
        userId: user?.id,
        accessToken: user?.accessToken,
        id: product?.Wishlists.find((item) => item.productId === product?.id)?.id,
      })
        .then(() => {
          setOpenSnackBar(true);
          setSnackBarMessage("Successfully removed from wish list");
          setIsError(false);
          setIsAdded(false); // Update wishlist status
        })
        .catch((err) => {})
        .finally(() => {
          setIsDeleting(false);
        });
    } else {
      removeFromWishlist(product.id)
      setOpenSnackBar(true);
      setSnackBarMessage("Successfully removed from wish list");
      setIsError(false);
    }
  };

  const handleMouseEnter = () => {
    if (product.imageTwo) {
      setCurrentImage(product.imageTwo);
    }
  };

  const handleMouseLeave = () => {
    setCurrentImage(product.imageOne);
  };

  const handleQuickCart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowQuickCart(!showQuickCart);
  };

  const isBespoke = product?.Type?.Subcategory?.Category?.name === "Bespoke";

  return (
    <>
      <Fade bottom>
        <a className="my-4 max-md:my-2 relative" href={`/products/${product?.slug}`}>
          <div className="cursor-pointer group hover:shadow-md relative rounded-b-md h-full p-2 ">
            <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="relative rounded-t-md"
              style={{ overflow: "hidden" }}
            >
              <img
                src={currentImage}
                className="aspect-square w-full rounded-t-md object-cover group-hover:scale-110 transition-all duration-300 ease-in-out"
                alt=""
                loading="lazy"
              />
              {!isBespoke && (
                <div onClick={(e) => handleQuickCart(e)} className="absolute bottom-4 right-4 z-10 !group-hover:opacity-100 hover:opacity-100 hover:bg-black bg-white rounded-full p-2 transition-all duration-300 ease-in-out shadow-md">
                  <BsCartPlus
                    className={classNames("text-orange-400 text-3xl")}
                  />
                </div>
              )}
            </div>
            <div className="absolute -left-1 top-0 max-sm:-top-[5px] space-y-2">
              {product?.isNewArrival && (
                <span className="text-[12px] max-md:text-[10px] text-white font-[500] p-2 bg-[#318EC9]">
                  New
                </span>
              )}
              <CustomDiscountDisplay discountPrice={product?.discountPrice} />
            </div>
            <div className="absolute -top-[10px] right-0">
              {!isBespoke && (
                <CustomOutOfStockDisplay quantity={product?.quantity} />
              )}
            </div>
            <div className="px-2 py-2">
              <div className="flex flex-wrap md:text-nowrap gap-1 items-start justify-between font-[500] text-[16px] max-lg:text-[14px] max-md:text-[12px]">
                <p title={product?.name} className="text-left text-sm text-gray-500 font-normal truncate flex-1">
                  {product?.name?.length > 20
                    ? `${product?.name?.slice(0, 20)}...`
                    : product?.name}
                </p>
                {isBespoke && <span className="px-2 py-1 text-xs font-normal bg-gray-300 rounded-full text-gray-600">custom made</span>}
              </div>
              <div className="text-xl font-semibold">
                {isBespoke ? (
                  product?.min > 0 && product?.max > 0 ? (
                    <div className="flex gap-1 items-center">
                      <div className="text-left">
                        <span className="text-xs">₦</span>
                        <span>{formatNumber(product.min)}</span>
                      </div>
                      -
                      <div className="text-left">
                        <span className="text-xs">₦</span>
                        <span>{formatNumber(product.max)}</span>
                      </div>
                    </div>
                  ) : (
                    ""
                  )
                ) : product?.priceAfterDiscount > 0 ? (
                  <div className="flex gap-2 items-center">
                    <div className="text-left"><span className="text-xs">₦</span><span>{formatNumber(product?.priceAfterDiscount)}</span></div>
                    <div
                      className="text-gray-500 text-xs font-normal"
                      style={{ textDecoration: "line-through" }}
                    >
                      {product?.discountPrice ? (
                        <CustomPriceDisplay price={product?.price} />
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </div>
              {!isBespoke && (
                <div className="flex flex-wrap gap-1 justify-between mt-1 font-[500] text-[12px]">
                  <div className="flex gap-1 items-center w-fit overflow-x-auto">
                    <p className="max-sm:text-[10px]">Colors:</p>
                    <div className="flex gap-1 items-center">
                      {product?.colors?.split(",")?.map((color, idx) => {
                        return (
                          <div
                            key={idx}
                            className={`w-[10px] h-[10px] rounded-full border-[0.5px] border-[#888888]`}
                            style={{ backgroundColor: color?.toLowerCase() }}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-1 flex max-sm:flex-col max-sm:items-start gap-1 justify-between items-center">
                <a className="" href={`/product-types/${product?.Type?.slug}`}>
                  <div className="font-[500] text-left max-sm:text-[10px]">
                    Type: {product?.Type?.name}
                  </div>
                </a>
                <div className="max-sm:text-[10px]">
                  Brand: {product?.Vendor?.businessName}
                </div>
              </div>
            </div>
          </div>
          <div className="absolute top-4 right-4 z-10 !group-hover:opacity-100">
            <IoHeartSharp
              onClick={(e) => {
                isAdded ? handleDeleteItem(e) : handleAddToWishlist(e);
              }}
              className={classNames(isAdded ? "text-orange-400" : "text-gray-400")}
              style={{ fontSize: "2.5em" }}
            />
          </div>
        </a>
      </Fade>
      <CustomSnackBar
        isOpen={openSnackBar}
        onClose={() => setOpenSnackBar(false)}
        message={snackBarMessage}
        isError={isError}
      />
      <GeneralModal
        isOpen={showQuickCart}
        onClose={() => setShowQuickCart(false)}
        widthClass="w-[75%] max-lg:w-[80%] max-md:w-[90%]  p-4"
      >
        <div className="h-[90vh] overflow-y-auto px-3">
          <ProductDetailsSection product={product} />
        </div>
      </GeneralModal>
    </>
  );
}

export default ProductItemMini;